import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Edit3, Trash2, Save, X, MapPin, Users, CreditCard, Star } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SPORT_OPTIONS = ["cricket", "baseball"];
const MACHINE_OPTIONS = ["bola_pro", "iron_mike"];
const DAY_OPTIONS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_OPTIONS = [
  { id: "live", label: "Live" },
  { id: "coming_soon", label: "Coming soon" },
];
const PERIOD_OPTIONS = [
  { id: "session", label: "/ session" },
  { id: "month", label: "/ month" },
  { id: "block", label: "/ block" },
];

const subTabs = [
  { id: "venues", label: "Venues", icon: MapPin },
  { id: "coaches", label: "Coaches", icon: Users },
  { id: "memberships", label: "Memberships", icon: CreditCard },
];

const inputCls =
  "w-full bg-white border border-slate-200 px-3 py-2 text-white text-sm font-exo focus:border-spike-blue focus:outline-none";
const labelCls =
  "block text-[0.6rem] font-orbitron font-bold uppercase tracking-[0.25em] text-spike-blue-light mb-1.5";

const ChipMulti = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((opt) => {
      const id = typeof opt === "string" ? opt : opt.id;
      const label = typeof opt === "string" ? opt : opt.label;
      const active = value.includes(id);
      return (
        <button
          key={id}
          type="button"
          data-testid={`${testid}-${id}`}
          onClick={() =>
            onChange(active ? value.filter((v) => v !== id) : [...value, id])
          }
          className={`px-3 py-1.5 border font-orbitron text-[0.65rem] uppercase tracking-[0.2em] transition-colors ${
            active
              ? "bg-spike-blue text-white border-spike-blue"
              : "bg-white text-slate-600 border-slate-200 hover:border-spike-blue"
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const TextList = ({ value, onChange, placeholder, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {value.map((v, idx) => (
      <div key={idx} className="flex gap-2">
        <input
          type="text"
          value={v}
          data-testid={`${testid}-input-${idx}`}
          onChange={(e) => {
            const next = [...value];
            next[idx] = e.target.value;
            onChange(next);
          }}
          className={inputCls}
          placeholder={placeholder}
        />
        <button
          type="button"
          data-testid={`${testid}-remove-${idx}`}
          onClick={() => onChange(value.filter((_, i) => i !== idx))}
          className="px-3 border border-slate-200 text-slate-600 hover:text-white hover:border-red-500/40"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    ))}
    <button
      type="button"
      data-testid={`${testid}-add`}
      onClick={() => onChange([...value, ""])}
      className="inline-flex items-center gap-2 border border-dashed border-slate-200 hover:border-spike-blue text-slate-600 hover:text-white px-3 py-1.5 font-orbitron text-[0.6rem] uppercase tracking-[0.2em]"
    >
      <Plus className="w-3 h-3" /> Add
    </button>
  </div>
);

// ---------- VENUES ----------
const emptyVenue = {
  id: "",
  name: "",
  city: "",
  address: "",
  phone: "",
  email: "",
  status: "live",
  lanes: 4,
  sports: ["cricket"],
  machines: [],
  image: "",
  lane_price_cad: 35,
  box_price_cad: 100,
};

const VenueForm = ({ initial, onCancel, onSaved }) => {
  const [v, setV] = useState({ ...emptyVenue, ...initial });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!v.name || !v.city || !v.lanes || v.sports.length === 0) {
      toast.error("Name, city, lanes, and at least one sport are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: v.name,
        city: v.city,
        address: v.address,
        phone: v.phone,
        email: v.email,
        status: v.status,
        lanes: Number(v.lanes),
        sports: v.sports,
        machines: v.machines,
        image: v.image,
        lane_price_cad: Number(v.lane_price_cad),
        box_price_cad: Number(v.box_price_cad),
      };
      if (isEdit) {
        await api.patch(`/admin/venues/${initial.id}`, payload, { withCredentials: true });
        toast.success("Venue updated.");
      } else {
        await api.post("/admin/venues", payload, { withCredentials: true });
        toast.success("Venue created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save venue.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/30 p-6 space-y-5" data-testid="venue-form">
      <div className="font-orbitron font-bold uppercase tracking-wider text-white text-sm">
        {isEdit ? `Edit · ${initial.id}` : "New venue"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="venue-input-name" className={inputCls} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>City</label>
          <input data-testid="venue-input-city" className={inputCls} value={v.city} onChange={(e) => setV({ ...v, city: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Address</label>
          <input data-testid="venue-input-address" className={inputCls} value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input data-testid="venue-input-phone" className={inputCls} value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="venue-input-email" className={inputCls} value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Lanes</label>
          <input
            type="number"
            min={1}
            max={50}
            data-testid="venue-input-lanes"
            className={inputCls}
            value={v.lanes}
            onChange={(e) => setV({ ...v, lanes: e.target.value })}
          />
          <div className="mt-1 text-[0.6rem] font-orbitron uppercase tracking-[0.25em] text-spike-dim">
            1 box = 3 consecutive lanes
          </div>
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select
            data-testid="venue-input-status"
            className={inputCls}
            value={v.status}
            onChange={(e) => setV({ ...v, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Lane price (CAD / hr)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            data-testid="venue-input-lane-price"
            className={inputCls}
            value={v.lane_price_cad}
            onChange={(e) => setV({ ...v, lane_price_cad: e.target.value })}
          />
        </div>
        <div>
          <label className={labelCls}>Box price (CAD / hr)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            data-testid="venue-input-box-price"
            className={inputCls}
            value={v.box_price_cad}
            onChange={(e) => setV({ ...v, box_price_cad: e.target.value })}
          />
          <div className="mt-1 text-[0.6rem] font-orbitron uppercase tracking-[0.25em] text-spike-dim">
            1 box = 3 lanes booked together
          </div>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Image URL</label>
          <input data-testid="venue-input-image" className={inputCls} value={v.image} onChange={(e) => setV({ ...v, image: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Sports</label>
          <ChipMulti options={SPORT_OPTIONS} value={v.sports} onChange={(next) => setV({ ...v, sports: next })} testid="venue-sports" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Machines</label>
          <ChipMulti options={MACHINE_OPTIONS} value={v.machines} onChange={(next) => setV({ ...v, machines: next })} testid="venue-machines" />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          data-testid="venue-save"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-5 py-2.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
        <button
          type="button"
          data-testid="venue-cancel"
          onClick={onCancel}
          className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-5 py-2.5"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const VenuesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/venues", { withCredentials: true });
      setItems(data);
    } catch {
      toast.error("Could not load venues.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm(`Delete venue "${id}"? Existing bookings won't be deleted.`)) return;
    try {
      await api.delete(`/admin/venues/${id}`, { withCredentials: true });
      toast.success("Venue deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (loading) {
    return <div className="py-12 flex justify-center text-slate-600"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="venues-panel">
      <div className="flex justify-between items-center">
        <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.3em] text-spike-blue-light">
          {items.length} venue{items.length !== 1 ? "s" : ""}
        </div>
        {!creating && !editing && (
          <button
            type="button"
            data-testid="venue-add-new"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-4 py-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add venue
          </button>
        )}
      </div>

      {creating && (
        <VenueForm
          initial={null}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); reload(); }}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-100">
        {items.map((v) => (
          <div key={v.id} className="bg-spike-elevated p-5" data-testid={`venue-card-${v.id}`}>
            {editing === v.id ? (
              <VenueForm
                initial={v}
                onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); reload(); }}
              />
            ) : (
              <>
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.3em] text-spike-blue-light">
                      {v.status === "live" ? "● Live" : "Coming soon"}
                    </div>
                    <div className="mt-1 font-orbitron font-bold uppercase text-white text-base">{v.name}</div>
                    <div className="mt-0.5 font-exo text-xs text-slate-600">
                      {v.city} · {v.lanes} lanes · {Math.floor(v.lanes / 3)} box{Math.floor(v.lanes / 3) !== 1 ? "es" : ""}
                    </div>
                    <div className="mt-1 font-orbitron text-[0.65rem] uppercase tracking-wider text-spike-blue-light">
                      Lane ${v.lane_price_cad ?? 35} · Box ${v.box_price_cad ?? 100} / hr
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {v.sports.map((s) => (
                        <span key={s} className="text-[0.6rem] font-orbitron uppercase tracking-wider px-2 py-0.5 border border-spike-blue/30 text-spike-blue-light">{s}</span>
                      ))}
                      {v.machines.map((m) => (
                        <span key={m} className="text-[0.6rem] font-orbitron uppercase tracking-wider px-2 py-0.5 border border-slate-200 text-slate-600">{m.replace("_", " ")}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      data-testid={`venue-edit-${v.id}`}
                      onClick={() => setEditing(v.id)}
                      className="p-2 border border-slate-200 text-slate-600 hover:text-white hover:border-spike-blue"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`venue-delete-${v.id}`}
                      onClick={() => remove(v.id)}
                      className="p-2 border border-slate-200 text-slate-600 hover:text-red-300 hover:border-red-500/40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- COACHES ----------
const emptyCoach = {
  id: "",
  name: "",
  role: "",
  bio: "",
  image: "",
  hourly_rate_cad: 75,
  sports: ["cricket"],
  venues: [],
  available_days: [],
};

const CoachForm = ({ initial, venues, onCancel, onSaved }) => {
  const [c, setC] = useState({ ...emptyCoach, ...initial });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!c.name || c.sports.length === 0 || c.venues.length === 0) {
      toast.error("Name, at least one sport, and at least one venue are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: c.name,
        role: c.role,
        bio: c.bio,
        image: c.image,
        hourly_rate_cad: Number(c.hourly_rate_cad),
        sports: c.sports,
        venues: c.venues,
        available_days: c.available_days,
      };
      if (isEdit) {
        await api.patch(`/admin/coaches/${initial.id}`, payload, { withCredentials: true });
        toast.success("Coach updated.");
      } else {
        await api.post("/admin/coaches", payload, { withCredentials: true });
        toast.success("Coach created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/30 p-6 space-y-5" data-testid="coach-form">
      <div className="font-orbitron font-bold uppercase tracking-wider text-white text-sm">
        {isEdit ? `Edit · ${initial.id}` : "New coach"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="coach-input-name" className={inputCls} value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Role</label>
          <input data-testid="coach-input-role" className={inputCls} value={c.role} onChange={(e) => setC({ ...c, role: e.target.value })} placeholder="Head Cricket Coach" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Bio</label>
          <textarea data-testid="coach-input-bio" rows={3} className={`${inputCls} resize-none`} value={c.bio} onChange={(e) => setC({ ...c, bio: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Image URL</label>
          <input data-testid="coach-input-image" className={inputCls} value={c.image} onChange={(e) => setC({ ...c, image: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Hourly rate (CAD)</label>
          <input
            type="number"
            min={0}
            data-testid="coach-input-rate"
            className={inputCls}
            value={c.hourly_rate_cad}
            onChange={(e) => setC({ ...c, hourly_rate_cad: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Sports</label>
          <ChipMulti options={SPORT_OPTIONS} value={c.sports} onChange={(next) => setC({ ...c, sports: next })} testid="coach-sports" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Venues</label>
          <ChipMulti
            options={venues.map((v) => ({ id: v.id, label: v.city }))}
            value={c.venues}
            onChange={(next) => setC({ ...c, venues: next })}
            testid="coach-venues"
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Available days</label>
          <ChipMulti options={DAY_OPTIONS} value={c.available_days} onChange={(next) => setC({ ...c, available_days: next })} testid="coach-days" />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" data-testid="coach-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-5 py-2.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
        <button type="button" data-testid="coach-cancel" onClick={onCancel} className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-5 py-2.5">
          Cancel
        </button>
      </div>
    </div>
  );
};

const CoachesPanel = () => {
  const [items, setItems] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v] = await Promise.all([
        api.get("/admin/coaches", { withCredentials: true }),
        api.get("/admin/venues", { withCredentials: true }),
      ]);
      setItems(c.data);
      setVenues(v.data);
    } catch {
      toast.error("Could not load coaches.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm(`Delete coach "${id}"?`)) return;
    try {
      await api.delete(`/admin/coaches/${id}`, { withCredentials: true });
      toast.success("Coach deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>;

  return (
    <div className="space-y-5" data-testid="coaches-panel">
      <div className="flex justify-between items-center">
        <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.3em] text-spike-blue-light">
          {items.length} coach{items.length !== 1 ? "es" : ""}
        </div>
        {!creating && !editing && (
          <button
            type="button"
            data-testid="coach-add-new"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-4 py-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add coach
          </button>
        )}
      </div>

      {creating && (
        <CoachForm initial={null} venues={venues} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-100">
        {items.map((c) => (
          <div key={c.id} className="bg-spike-elevated p-5" data-testid={`coach-card-${c.id}`}>
            {editing === c.id ? (
              <CoachForm initial={c} venues={venues} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
            ) : (
              <div className="flex gap-4 justify-between">
                <div className="flex gap-4 min-w-0">
                  {c.image ? <img src={c.image} alt={c.name} className="w-16 h-16 object-cover grayscale shrink-0" /> : <div className="w-16 h-16 bg-white border border-slate-200 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-orbitron font-bold uppercase text-white text-sm tracking-wider">{c.name}</div>
                    <div className="mt-0.5 font-exo text-xs text-slate-600">{c.role}</div>
                    <div className="mt-1.5 font-orbitron text-[0.65rem] uppercase tracking-wider text-spike-blue-light">${c.hourly_rate_cad}/hr · {c.sports.join(" · ")}</div>
                    <div className="mt-1 font-exo text-xs text-spike-dim">{c.venues.join(", ")}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button type="button" data-testid={`coach-edit-${c.id}`} onClick={() => setEditing(c.id)} className="p-2 border border-slate-200 text-slate-600 hover:text-white hover:border-spike-blue">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" data-testid={`coach-delete-${c.id}`} onClick={() => remove(c.id)} className="p-2 border border-slate-200 text-slate-600 hover:text-red-300 hover:border-red-500/40">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- MEMBERSHIPS ----------
const emptyTier = {
  id: "",
  name: "",
  tagline: "",
  price_cad: 49,
  period: "month",
  highlight: false,
  features: [""],
  cta: "Choose plan",
  discount_percent: 0,
  monthly_free_hours: 0,
};

const TierForm = ({ initial, onCancel, onSaved }) => {
  const [t, setT] = useState({ ...emptyTier, ...initial, features: initial?.features?.length ? initial.features : [""] });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    const cleanedFeatures = t.features.map((f) => f.trim()).filter(Boolean);
    if (!t.name || !cleanedFeatures.length) {
      toast.error("Name and at least one feature are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: t.name,
        tagline: t.tagline,
        price_cad: Number(t.price_cad),
        period: t.period,
        highlight: t.highlight,
        features: cleanedFeatures,
        cta: t.cta || "Choose plan",
        discount_percent: Math.max(0, Math.min(100, Number(t.discount_percent) || 0)),
        monthly_free_hours: Math.max(0, Math.min(200, Number(t.monthly_free_hours) || 0)),
      };
      if (isEdit) {
        await api.patch(`/admin/memberships/${initial.id}`, payload, { withCredentials: true });
        toast.success("Tier updated.");
      } else {
        await api.post("/admin/memberships", payload, { withCredentials: true });
        toast.success("Tier created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/30 p-6 space-y-5" data-testid="tier-form">
      <div className="font-orbitron font-bold uppercase tracking-wider text-white text-sm">
        {isEdit ? `Edit · ${initial.id}` : "New membership tier"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="tier-input-name" className={inputCls} value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Tagline</label>
          <input data-testid="tier-input-tagline" className={inputCls} value={t.tagline} onChange={(e) => setT({ ...t, tagline: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Price (CAD)</label>
          <input
            type="number"
            min={0}
            data-testid="tier-input-price"
            className={inputCls}
            value={t.price_cad}
            onChange={(e) => setT({ ...t, price_cad: e.target.value })}
          />
        </div>
        <div>
          <label className={labelCls}>Period</label>
          <select data-testid="tier-input-period" className={inputCls} value={t.period} onChange={(e) => setT({ ...t, period: e.target.value })}>
            {PERIOD_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>CTA text</label>
          <input data-testid="tier-input-cta" className={inputCls} value={t.cta} onChange={(e) => setT({ ...t, cta: e.target.value })} />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="tier-input-highlight"
              checked={t.highlight}
              onChange={(e) => setT({ ...t, highlight: e.target.checked })}
              className="w-4 h-4 accent-spike-blue"
            />
            <span className="font-orbitron text-[0.65rem] uppercase tracking-[0.25em] text-white">Highlight tier</span>
          </label>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Features</label>
          <TextList value={t.features} onChange={(next) => setT({ ...t, features: next })} placeholder="e.g. 8 sessions / month" testid="tier-features" />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" data-testid="tier-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-5 py-2.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
        <button type="button" data-testid="tier-cancel" onClick={onCancel} className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-5 py-2.5">
          Cancel
        </button>
      </div>
    </div>
  );
};

const MembershipsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/memberships", { withCredentials: true });
      setItems(data);
    } catch {
      toast.error("Could not load tiers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm(`Delete tier "${id}"?`)) return;
    try {
      await api.delete(`/admin/memberships/${id}`, { withCredentials: true });
      toast.success("Tier deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>;

  return (
    <div className="space-y-5" data-testid="tiers-panel">
      <div className="flex justify-between items-center">
        <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.3em] text-spike-blue-light">
          {items.length} tier{items.length !== 1 ? "s" : ""}
        </div>
        {!creating && !editing && (
          <button
            type="button"
            data-testid="tier-add-new"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] px-4 py-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add tier
          </button>
        )}
      </div>

      {creating && (
        <TierForm initial={null} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-100">
        {items.map((t) => (
          <div key={t.id} className="bg-spike-elevated p-5" data-testid={`tier-card-${t.id}`}>
            {editing === t.id ? (
              <TierForm initial={t} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
            ) : (
              <div className="flex justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {t.highlight && <Star className="w-3.5 h-3.5 text-spike-blue" strokeWidth={2.5} />}
                    <span className="font-orbitron font-bold uppercase text-white text-base">{t.name}</span>
                  </div>
                  <div className="mt-0.5 font-exo text-xs text-slate-600 italic">{t.tagline}</div>
                  <div className="mt-2 font-orbitron font-black text-2xl text-white">${t.price_cad}<span className="text-slate-600 text-sm font-exo font-normal"> / {t.period}</span></div>
                  <ul className="mt-3 space-y-1 font-exo text-xs text-slate-600">
                    {t.features.map((f, i) => <li key={i}>· {f}</li>)}
                  </ul>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button type="button" data-testid={`tier-edit-${t.id}`} onClick={() => setEditing(t.id)} className="p-2 border border-slate-200 text-slate-600 hover:text-white hover:border-spike-blue">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" data-testid={`tier-delete-${t.id}`} onClick={() => remove(t.id)} className="p-2 border border-slate-200 text-slate-600 hover:text-red-300 hover:border-red-500/40">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- ROOT ----------
const AdminConfig = () => {
  const [sub, setSub] = useState("venues");

  return (
    <div className="space-y-6" data-testid="admin-config-tab">
      <div className="flex gap-px bg-slate-100 w-fit" data-testid="config-subtabs">
        {subTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`config-subtab-${t.id}`}
              onClick={() => setSub(t.id)}
              className={`inline-flex items-center gap-2 px-5 py-3 font-orbitron text-[0.7rem] uppercase tracking-[0.25em] transition-colors ${
                sub === t.id ? "bg-spike-blue text-white" : "bg-white text-slate-600 hover:text-white"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {sub === "venues" && <VenuesPanel />}
      {sub === "coaches" && <CoachesPanel />}
      {sub === "memberships" && <MembershipsPanel />}
    </div>
  );
};

export default AdminConfig;
